/**
 * Regional Price Component
 * Displays product price in user's selected currency
 */

import React, { useState, useEffect } from 'react';
import { useRegional } from '../../context/RegionalContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegionalPrice = ({ 
  productId, 
  basePriceUSD, 
  className = '',
  showOriginal = false,
  size = 'default' // 'small', 'default', 'large'
}) => {
  const { region } = useRegional();
  const [priceInfo, setPriceInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch price whenever region or product changes - key fix: force refetch on region change
  useEffect(() => {
    let isMounted = true;
    
    const fetchPrice = async () => {
      if (!productId || !basePriceUSD) {
        setLoading(false);
        return;
      }
      
      // Reset price info immediately to show loading state
      setPriceInfo(null);
      setLoading(true);
      
      try {
        // Add cache-busting timestamp to force fresh data
        const response = await fetch(
          `${API_URL}/api/regional/price/${productId}?region_code=${region.code}&_t=${Date.now()}`
        );
        if (response.ok && isMounted) {
          const data = await response.json();
          setPriceInfo(data.regional_price);
        }
      } catch (error) {
        console.warn('Failed to fetch regional price:', error);
        // Fallback to base price with current region symbol
        if (isMounted) {
          setPriceInfo({
            price: basePriceUSD,
            price_formatted: `${region.symbol}${basePriceUSD.toFixed(2)}`,
            currency: region.currency,
            currency_symbol: region.symbol,
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPrice();
    
    return () => {
      isMounted = false;
    };
  }, [productId, basePriceUSD, region.code, region.symbol, region.currency]);

  // Size classes
  const sizeClasses = {
    small: 'text-sm',
    default: 'text-lg',
    large: 'text-2xl md:text-3xl',
  };

  if (loading) {
    return (
      <span className={`animate-pulse bg-slate-200 rounded h-6 w-20 inline-block ${className}`} />
    );
  }

  if (!priceInfo) {
    return (
      <span className={`font-semibold ${sizeClasses[size]} ${className}`}>
        ${basePriceUSD?.toFixed(2)}
      </span>
    );
  }

  return (
    <span className={`font-semibold ${sizeClasses[size]} ${className}`}>
      {priceInfo.price_formatted}
      {showOriginal && region.code !== 'US' && (
        <span className="text-xs text-slate-400 ml-2 font-normal">
          (${basePriceUSD?.toFixed(2)} USD)
        </span>
      )}
    </span>
  );
};

/**
 * Simple price display using current region formatting
 */
export const FormattedPrice = ({ price, className = '', size = 'default' }) => {
  const { region } = useRegional();
  
  const sizeClasses = {
    small: 'text-sm',
    default: 'text-lg',
    large: 'text-2xl md:text-3xl',
  };

  return (
    <span className={`font-semibold ${sizeClasses[size]} ${className}`}>
      {region.symbol}{Number(price).toFixed(2)}
    </span>
  );
};

/**
 * Tax info display
 */
export const TaxInfo = ({ className = '' }) => {
  const { region } = useRegional();
  
  if (region.code === 'US') {
    return null;
  }

  return (
    <span className={`text-xs text-slate-500 ${className}`}>
      {region.code === 'GB' ? 'Inc. VAT' : ''}
    </span>
  );
};

export default RegionalPrice;
